import { createContext, useContext, useEffect, useMemo, useState, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  FALLBACK_COUNTRIES,
  FALLBACK_CURRENCIES,
  convertWithRates,
  countryToCurrency,
  currencyFromBrowserLocale,
  formatApprox,
  formatMoney,
  registerCurrencies,
  type CountryDef,
  type CurrencyDef,
} from "@/lib/money";

const STORAGE_KEY = "gloogum_display_currency";
const COUNTRY_KEY = "gloogum_country";
const SECONDARY_KEY = "gloogum_secondary_currency";
const SHOW_CONVERSION_KEY = "gloogum_show_conversion";
const RATES_CACHE_KEY = "gloogum_fx_rates_cache_v1";
const CACHE_TTL_MS = 1000 * 60 * 60; // 1h

const SUPPORTED = [
  "USD","EUR","GBP","NGN","GHS","KES","ZAR","CAD","AUD","JPY","CNY","INR",
  "BRL","MXN","AED","SGD","CHF","SEK","NOK","DKK","PLN","TRY","EGP","XOF",
  "XAF","TZS","UGX","RWF","ETB","MAD","TND",
] as const;
export type SupportedCurrency = typeof SUPPORTED[number];

const SYMBOLS: Record<string, string> = {
  USD: "$", EUR: "€", GBP: "£", NGN: "₦", GHS: "₵", KES: "KSh",
  ZAR: "R", CAD: "CA$", AUD: "A$", JPY: "¥", CNY: "¥", INR: "₹",
};

interface CurrencyCtx {
  /** User's chosen display currency. Prices in app are stored in USD (base). */
  currency: SupportedCurrency;
  setCurrency: (c: SupportedCurrency) => void;
  /** rates map: 1 USD = rates[XYZ] XYZ */
  rates: Record<string, number>;
  loading: boolean;
  /** Convert a USD amount into the user's display currency. */
  convert: (usdAmount: number) => number;
  /** Format a USD amount into a localized currency string. */
  format: (usdAmount: number, opts?: { showCode?: boolean }) => string;
  supported: readonly string[];
  refresh: () => Promise<void>;
  /* ---- Multi-currency (source-of-truth aware) API ---- */
  /** Country the user is browsing as (drives default transaction currency). */
  country: string | null;
  setCountry: (c: string | null) => void;
  /** Optional secondary/reference currency for "≈" estimates. */
  secondaryCurrency: string;
  setSecondaryCurrency: (c: string) => void;
  showConversion: boolean;
  setShowConversion: (v: boolean) => void;
  countries: CountryDef[];
  currencies: CurrencyDef[];
  /** Currency a *new* price should be created in for this user. */
  transactionCurrency: string;
  /** Convert between any two currencies. Returns null when no rate is known. */
  convertFrom: (amount: number, from: string, to?: string) => number | null;
  /** Format an amount in its OWN stored currency (authoritative price). */
  formatIn: (amount: number, currency: string, opts?: { showCode?: boolean; compact?: boolean }) => string;
  /** "≈ C$12.30" estimate of a stored amount, or "" when unavailable/disabled. */
  approxIn: (amount: number, sourceCurrency: string, target?: string) => string;
}

const CurrencyContext = createContext<CurrencyCtx | null>(null);

function loadCache(): { rates: Record<string, number>; ts: number } | null {
  try {
    const raw = localStorage.getItem(RATES_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.rates || !parsed?.ts) return null;
    return parsed;
  } catch { return null; }
}

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrencyState] = useState<SupportedCurrency>(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as SupportedCurrency | null;
    if (stored && (SUPPORTED as readonly string[]).includes(stored)) return stored;
    const fromCountry = countryToCurrency(localStorage.getItem(COUNTRY_KEY));
    const guess = fromCountry || currencyFromBrowserLocale();
    return (guess && (SUPPORTED as readonly string[]).includes(guess)
      ? guess
      : "USD") as SupportedCurrency;
  });
  const [country, setCountryState] = useState<string | null>(
    () => localStorage.getItem(COUNTRY_KEY),
  );
  const [secondaryCurrency, setSecondaryState] = useState<string>(
    () => localStorage.getItem(SECONDARY_KEY) || "USD",
  );
  const [showConversion, setShowConversionState] = useState<boolean>(
    () => localStorage.getItem(SHOW_CONVERSION_KEY) !== "false",
  );
  const [countries, setCountries] = useState<CountryDef[]>(FALLBACK_COUNTRIES);
  const [currencies, setCurrencies] = useState<CurrencyDef[]>(FALLBACK_CURRENCIES);
  const [rates, setRates] = useState<Record<string, number>>(() => loadCache()?.rates ?? { USD: 1 });
  const [loading, setLoading] = useState(false);

  /** Persist a preference to the signed-in user's profile (best effort). */
  const persist = async (patch: Record<string, unknown>) => {
    try {
      const { data } = await supabase.auth.getUser();
      if (!data.user) return;
      await supabase.from("profiles").update(patch).eq("id", data.user.id);
    } catch {
      /* guests keep local storage only */
    }
  };

  const setCurrency = (c: SupportedCurrency) => {
    setCurrencyState(c);
    localStorage.setItem(STORAGE_KEY, c);
    void persist({ preferred_currency: c });
  };

  const setCountry = (c: string | null) => {
    setCountryState(c);
    if (c) localStorage.setItem(COUNTRY_KEY, c);
    else localStorage.removeItem(COUNTRY_KEY);
    void persist({ preferred_country: c });
  };

  const setSecondaryCurrency = (c: string) => {
    setSecondaryState(c);
    localStorage.setItem(SECONDARY_KEY, c);
    void persist({ secondary_currency: c });
  };

  const setShowConversion = (v: boolean) => {
    setShowConversionState(v);
    localStorage.setItem(SHOW_CONVERSION_KEY, String(v));
    void persist({ show_currency_conversion: v });
  };

  const fetchRates = async () => {
    setLoading(true);
    // Hard timeout so a slow/unreachable function never spams the console with ERR_TIMED_OUT.
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 6000);
    try {
      const { data, error } = await supabase.functions.invoke("currency-convert", {
        body: { action: "get_rates", from_currency: "USD" },
      });
      if (!error && data?.rates) {
        setRates(data.rates);
        localStorage.setItem(RATES_CACHE_KEY, JSON.stringify({ rates: data.rates, ts: Date.now() }));
        return;
      }
      // Fall back to the platform's cached FX table (centralized FX service).
      const { data: cached } = await supabase.rpc("get_cached_fx_rates", { _base: "USD" });
      if (cached?.length) {
        const map: Record<string, number> = { USD: 1 };
        for (const row of cached as { target_currency: string; exchange_rate: number }[]) {
          map[row.target_currency] = Number(row.exchange_rate);
        }
        setRates(map);
        localStorage.setItem(RATES_CACHE_KEY, JSON.stringify({ rates: map, ts: Date.now() }));
      }
    } catch (e) {
      // Silent — fall back to cached/USD rates. Avoid noisy console for non-critical FX refresh.
    } finally {
      clearTimeout(timer);
      setLoading(false);
    }
  };

  useEffect(() => {
    const cache = loadCache();
    if (!cache || Date.now() - cache.ts > CACHE_TTL_MS) {
      // Defer so it never blocks LCP / critical render path.
      const run = () => fetchRates();
      const w: any = typeof window !== "undefined" ? window : null;
      if (w?.requestIdleCallback) w.requestIdleCallback(run, { timeout: 4000 });
      else setTimeout(run, 1500);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /** Load the admin-managed country/currency catalogue (falls back to static config). */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [cur, ctry] = await Promise.all([
          supabase
            .from("platform_currencies")
            .select("code,name,symbol,decimal_digits")
            .eq("is_active", true)
            .order("display_order"),
          supabase
            .from("platform_countries")
            .select("country_code,country_name,default_currency,locale,flag_emoji")
            .eq("is_active", true)
            .order("display_order"),
        ]);
        if (cancelled) return;
        if (cur.data?.length) {
          const mapped: CurrencyDef[] = cur.data.map((c: any) => ({
            code: c.code,
            name: c.name,
            symbol: c.symbol,
            decimals: c.decimal_digits ?? 2,
          }));
          registerCurrencies(mapped);
          setCurrencies(mapped);
        }
        if (ctry.data?.length) setCountries(ctry.data as CountryDef[]);
      } catch {
        /* keep static fallback */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /** Signed-in preferences win over local guesses. */
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const { data } = await supabase.auth.getUser();
      if (!data.user || cancelled) return;
      const { data: prof } = await supabase
        .from("profiles")
        .select("preferred_country,preferred_currency,secondary_currency,show_currency_conversion")
        .eq("id", data.user.id)
        .maybeSingle();
      if (!prof || cancelled) return;
      if (prof.preferred_currency) {
        setCurrencyState(prof.preferred_currency as SupportedCurrency);
        localStorage.setItem(STORAGE_KEY, prof.preferred_currency);
      }
      if (prof.preferred_country) {
        setCountryState(prof.preferred_country);
        localStorage.setItem(COUNTRY_KEY, prof.preferred_country);
      }
      if (prof.secondary_currency) setSecondaryState(prof.secondary_currency);
      if (typeof prof.show_currency_conversion === "boolean")
        setShowConversionState(prof.show_currency_conversion);
    };
    void load();
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      if (session?.user) void load();
    });
    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  const convert = (usdAmount: number) => {
    const r = rates[currency] ?? 1;
    return usdAmount * r;
  };

  const format = (usdAmount: number, opts?: { showCode?: boolean }) => {
    const value = convert(usdAmount);
    try {
      const formatted = new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
        maximumFractionDigits: 2,
      }).format(value);
      return opts?.showCode ? `${formatted} ${currency}` : formatted;
    } catch {
      const sym = SYMBOLS[currency] || currency + " ";
      return `${sym}${value.toFixed(2)}`;
    }
  };

  const value = useMemo<CurrencyCtx>(() => ({
    currency, setCurrency, rates, loading, convert, format,
    supported: SUPPORTED, refresh: fetchRates,
    country, setCountry, secondaryCurrency, setSecondaryCurrency,
    showConversion, setShowConversion, countries, currencies,
    transactionCurrency: countryToCurrency(country) || currency,
    convertFrom: (amount, from, to) => convertWithRates(amount, from, to || currency, rates),
    formatIn: (amount, cur, opts) => formatMoney(amount, cur, opts),
    approxIn: (amount, sourceCurrency, target) => {
      const to = target || currency;
      if (!showConversion) return "";
      if (!sourceCurrency || sourceCurrency.toUpperCase() === to.toUpperCase()) return "";
      const converted = convertWithRates(amount, sourceCurrency, to, rates);
      return converted == null ? "" : formatApprox(converted, to);
    },
  }), [currency, rates, loading, country, secondaryCurrency, showConversion, countries, currencies]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency() {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error("useCurrency must be used inside CurrencyProvider");
  return ctx;
}